import { Button, Span, Table } from "trunx";
import {
  decimalPlaces,
  formatUnit,
  useLocaleContext
} from "../../contexts/LocaleContext";

import classNames from "classnames";
import { scaleThreshold } from "d3-scale";
import { observer } from "mobx-react-lite";
import config from "../../config";
import convert from "../../convert";
import StreamMarker, { StreamM } from "../../layers/StreamMarker";
import { useRootStore } from "../../models/Root";
import { ISensorDisplayStream } from "../../models/SensorDisplay";

const StreamLegend = ({ display }: { display: ISensorDisplayStream }) => {
  const currentMeasure = display.measure;

  const {
    dict: {
      panel: {
        sensors: { legend: dict },
      },
    },
    units: {
      stream: { stage: stageUnit, discharge: dischargeUnit },
    },
  } = useLocaleContext();

  const {
    sensors: { displayStreamWithMeasure },
  } = useRootStore();

  const { backgroundColors, stageBreakValues, dischargeBreakValues } =
    config.markers.stream;
  const breakValues =
    currentMeasure === "stage" ? stageBreakValues : dischargeBreakValues;
  const colorScaleThreshold = scaleThreshold(breakValues, backgroundColors);

  const legendRows = backgroundColors.map((color) => {
    const [min, max] = colorScaleThreshold.invertExtent(color);
    if (max) {
      const min_ = min || 0;
      return {
        isLast: false,
        threshold: max,
        value: min_ + (max - min_) / 2.0,
      };
    } else if (min) {
      return { isLast: true, threshold: min, value: min };
    } else {
      throw new Error("invertExtent shouldn't do this");
    }
  });

  const unit = currentMeasure === "stage" ? stageUnit : dischargeUnit;

  const MeasureButton = ({
    measure,
  }: {
    measure: ISensorDisplayStream["measure"];
  }) => {
    const isSelected = currentMeasure === measure;
    const unit = measure === "stage" ? stageUnit : dischargeUnit;
    return (
      <Button
        onClick={() => displayStreamWithMeasure(measure)}
        className={classNames({
          "is-selected": isSelected,
          "is-primary": isSelected,
        })}
      >
        <span>{dict.streamMeasure[measure]}</span>
        &nbsp;
        <Span bulma="is-size-7"> ({formatUnit(unit)})</Span>
      </Button>
    );
  };

  const LegendRow = ({
    isLast,
    threshold,
    value,
  }: {
    isLast: boolean;
    threshold: number;
    value: number;
  }) => {
    return (
      <tr key={value}>
        <td key="marker" style={{ backgroundColor: "darkgrey" }}>
          <StreamMarker
            measure={currentMeasure}
            value={value}
            unit={currentMeasure === "stage" ? "ft" : "ft3/s"}
          ></StreamMarker>
        </td>
        <td key="threshold">
          {isLast ? "≥" : "<"}{" "}
          {convert(threshold)
            .from("ft")
            .to(stageUnit)
            .toFixed(decimalPlaces(stageUnit))}{" "}
          {formatUnit(unit)}
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="buttons has-addons" style={{ justifyContent: "center" }}>
        <MeasureButton measure={"stage"}></MeasureButton>
        <MeasureButton measure={"discharge"}></MeasureButton>
      </div>

      <Table
        bulma={["is-fullwidth", "is-narrow"]}
        style={{ marginTop: 0, textAlign: "center" }}
      >
        <tbody>
          <tr key="missing">
            <td style={{ backgroundColor: "darkgrey" }}>
              <StreamM />
            </td>
            <td>{dict.missing}</td>
          </tr>
          {legendRows.map((rowProps) => (
            <LegendRow key={rowProps.value} {...rowProps}></LegendRow>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default observer(StreamLegend);
